import { Box, Heading, HStack, Text } from "@chakra-ui/layout";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  SkeletonText,
  useToken,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type {
  ProfitLossReportColumns,
  ProfitLossReportRows,
} from "@src/__generated__/graphql";
import { ProfitAndLossReportStore } from "@src/components/modules/reports/profit-and-loss/store";
import {
  DataTable,
  DropdownButton,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { ModuleTitle } from "@src/components/ui-kit/ModuleTitle";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import type { NavigationReportsProps } from "@src/components/widgets/AppSubNav/NavigationReports/NavigationReports";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { upperFirst } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import { Fragment } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type Props = {
  report: NavigationReportsProps["currentReport"];
};
export const ProfitAndLossReport = observer(function ProfitAndLossReport({
  report,
}: Props) {
  const [violet, jade, red] = useToken("colors", [
    "violet.500",
    "jade.500",
    "red.500",
  ]);
  const appStore = useStore();
  const store = useLocalObservable(
    () => new ProfitAndLossReportStore(appStore),
  );

  return (
    <Box>
      <Box mb="4">
        <ModuleTitle>
          <Text fontWeight="inherit">{report?.title()}</Text>
        </ModuleTitle>
      </Box>

      <DataTable>
        <DataTable.NavBar>
          <Box py="4">
            <FiltersPanelRoot filters={store.where}>
              <HStack spacing="2" justify="space-between" mb="2">
                <HStack spacing="2">
                  <PeriodPicker
                    onChange={store.handleChangeDateRange}
                    value={store.dateRange}
                  />

                  <Menu>
                    <MenuButton as={DropdownButton} minW="fit-content">
                      <Trans>Rows</Trans>
                      <Fragment>
                        <span>:</span>
                        <Text as="span" ml="1" color="violet.500">
                          {upperFirst(store.rows.toLowerCase())}
                        </Text>
                      </Fragment>
                    </MenuButton>
                    <MenuList>
                      {store.rowsOptions.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          onClick={() => {
                            store.handleChangeRows(
                              value as ProfitLossReportRows,
                            );
                          }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>

                  <Menu>
                    <MenuButton as={DropdownButton} minW="fit-content">
                      <Trans>Columns</Trans>
                      <Fragment>
                        <span>:</span>
                        <Text as="span" ml="1" color="violet.500">
                          {upperFirst(
                            store.columns?.toLowerCase() ?? t`Selected range`,
                          )}
                        </Text>
                      </Fragment>
                    </MenuButton>
                    <MenuList>
                      {store.columnsOptions.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          onClick={() => {
                            store.handleChangeColumns(
                              value as ProfitLossReportColumns,
                            );
                          }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                      <MenuItem
                        onClick={() => {
                          store.handleChangeColumns(undefined);
                        }}
                      >
                        <Trans>Selected range</Trans>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>

                <FiltersPanelFilters />
              </HStack>
            </FiltersPanelRoot>
          </Box>
        </DataTable.NavBar>

        <DataTable.Stats>
          <Box w="full">
            <Skeleton
              w="full"
              h={250}
              isLoaded={!store.isLoadingSummary}
              rounded="md"
            >
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart
                  data={store.tableSummary.columns}
                  height={250}
                  width={250}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="title" />
                  <YAxis
                    width={100}
                    tickFormatter={(value) => currency.formatByWorkspace(value)}
                  />
                  <Tooltip formatter={store.graphTooltipFormatter} />
                  <Legend formatter={store.graphLegendFormatter} />
                  <Bar dataKey="expenses_amount" fill={red} />
                  <Bar dataKey="invoiced_amount" fill={jade} />
                  <Line
                    dataKey="balance_amount"
                    stroke={violet}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Skeleton>
            <HStack spacing="4" mt="4">
              <DataTable.StatCard>
                <Heading as="h3" size="md">
                  <Trans>Invoiced amount</Trans>
                </Heading>
                <SkeletonText
                  isLoaded={!store.isLoadingSummary}
                  noOfLines={1}
                  skeletonHeight="4"
                >
                  <Text mt="2">
                    {currency.formatByWorkspace(
                      store.tableSummary.total?.invoiced_amount ?? 0,
                    )}
                  </Text>
                </SkeletonText>
              </DataTable.StatCard>
              <DataTable.StatCard>
                <Heading as="h3" size="md">
                  <Trans>Expenses amount</Trans>
                </Heading>
                <SkeletonText
                  isLoaded={!store.isLoadingSummary}
                  noOfLines={1}
                  skeletonHeight="4"
                >
                  <Text mt="2">
                    {currency.formatByWorkspace(
                      store.tableSummary.total?.expenses_amount ?? 0,
                    )}
                  </Text>
                </SkeletonText>
              </DataTable.StatCard>
              <DataTable.StatCard>
                <Heading as="h3" size="md">
                  <Trans>Balance amount</Trans>
                </Heading>
                <SkeletonText
                  isLoaded={!store.isLoadingSummary}
                  noOfLines={1}
                  skeletonHeight="4"
                >
                  <Text mt="2">
                    {currency.formatByWorkspace(
                      store.tableSummary.total?.balance_amount ?? 0,
                    )}
                  </Text>
                </SkeletonText>
              </DataTable.StatCard>
            </HStack>
          </Box>
          <></>
        </DataTable.Stats>

        <Box pos="relative">
          <LoadingOverlay isLoading={store.isLoading} position="absolute">
            <DataTable.Body
              columns={store.tableColumns}
              data={store.tableData}
              tableKey={store.moduleKey}
            />
          </LoadingOverlay>
        </Box>

        <Pagination store={store.pagination} />
      </DataTable>
    </Box>
  );
});
